import { useCallback, useEffect, useState } from 'react';
import { Party, PartyDetail } from '@/network/RestApi';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useNotificationStore } from '@/state/notificationStore';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

const MAX_PARTY = 5;

export function PartyPanel() {
  const ch = useCharacterStore((s) => s.current);
  const close = () => useUIStore.getState().set('party', false);
  const showToast = useUIStore((s) => s.showToast);
  const refreshTick = useNotificationStore((s) => s.refreshTickParty);
  const [party, setParty] = useState<PartyDetail | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!ch) return;
    try {
      const r = await Party.ofCharacter(ch.id);
      if (r && (r as PartyDetail).id && (r as PartyDetail).id !== 0) {
        setParty(r as PartyDetail);
      } else {
        setParty(null);
      }
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  }, [ch, showToast]);

  useEffect(() => { reload(); }, [reload, refreshTick]);
  useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  if (!ch) return null;

  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await Party.create({ leaderId: ch.id });
      showToast('Tạo party OK');
      reload();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    if (!party || !name.trim() || busy) return;
    setBusy(true);
    try {
      await Party.invite(party.id, { byCharacterId: ch.id, targetName: name.trim() });
      showToast('Đã thêm vào party');
      setName('');
      reload();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!party || busy) return;
    setBusy(true);
    try {
      await Party.leave(party.id, { characterId: ch.id });
      showToast('Đã rời party');
      setParty(null);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const kick = async (targetId: number) => {
    if (!party || busy) return;
    setBusy(true);
    try {
      await Party.kick(party.id, { byCharacterId: ch.id, targetCharacterId: targetId });
      showToast('Đã đuổi');
      reload();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const isLeader = party && party.leaderId === ch.id;

  return (
    <div
      data-block-game-input="true"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'absolute', top: 70, right: 670, width: 320, ...ui.panel }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>Party</strong>
        <CloseButton onClick={close} />
      </div>

      {!party ? (
        <div>
          <p style={{ fontSize: 12, opacity: 0.7 }}>Chưa có party. Tạo mới để mời bạn bè (tối đa {MAX_PARTY} người, EXP +10%/thành viên).</p>
          <button style={{ ...ui.primaryBtn, marginTop: 8 }} disabled={busy} onClick={create}>Tạo party</button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
            Party #{party.id} ({party.members.length}/{MAX_PARTY})
            {isLeader && <span style={{ color: '#ffd166' }}> · Bạn là leader</span>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {party.members.map((m) => {
              const me = m.characterId === ch.id;
              const isLead = m.characterId === party.leaderId;
              return (
                <div key={m.characterId} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'rgba(0,0,0,0.25)', padding: '4px 8px', borderRadius: 4,
                }}>
                  <span style={{ fontSize: 13 }}>
                    {isLead && <span style={{ color: '#ffd166', marginRight: 4 }}>★</span>}
                    {m.name} · Lv {m.level} {m.class}
                    {me && <span style={{ opacity: 0.6 }}> (bạn)</span>}
                  </span>
                  {isLeader && !me && (
                    <button style={miniBtn('#b04a4a')} onClick={() => kick(m.characterId)}>X</button>
                  )}
                </div>
              );
            })}
          </div>

          {isLeader && party.members.length < MAX_PARTY && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                style={{ ...ui.input, flex: 1 }}
                placeholder="Tên người chơi..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') invite(); }}
              />
              <button style={ui.primaryBtn} disabled={busy} onClick={invite}>Mời</button>
            </div>
          )}

          <button style={{ ...ui.secondaryBtn, width: '100%' }} disabled={busy} onClick={leave}>
            Rời party
          </button>
        </>
      )}
    </div>
  );
}

function miniBtn(color: string): React.CSSProperties {
  return {
    background: color,
    color: '#fff',
    border: 'none',
    borderRadius: 3,
    padding: '2px 8px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 700,
  };
}
